import i18n from '@app/i18n';
import { RoutesStructureWithStringKeys } from '@app/types/routing.types';
import { Suspense } from 'react';
import { Navigate, Outlet, RouteObject, useLocation } from 'react-router-dom';

/**
 * The index route auto-added below, redirecting to a section's first visible child.
 *
 * A plain `<Navigate to={firstChildKey} replace />` drops the query string — a link that opens
 * a section on `?clientId=` (or any other param) would land on the child with it silently
 * gone. Reading `search` here and carrying it along fixes every section with children, not
 * only the one that motivated it.
 */
const IndexRedirect = ({ to }: { to: string }) => {
  const { search } = useLocation();
  return <Navigate to={{ pathname: to, search }} replace />;
};

const RoutingTransformer = (routesConfig: RoutesStructureWithStringKeys) => {
  const transformed = Object.keys(routesConfig)
    .filter((routeKey) => routesConfig[routeKey].show) // Only include routes marked as "show: true"
    .map((routeKey) => {
      const routeItem = routesConfig[routeKey];
      //
      // If the route has nested routes, create an Outlet and recursively transform child routes
      if (routeItem.routes) {
        // Find the first visible child route to use as index redirect
        const firstChildKey = Object.keys(routeItem.routes).find((childKey) => routeItem.routes?.[childKey]?.show);

        return {
          path: routeKey,
          element: (
            <Suspense fallback={<div>{i18n.t('common-loading')}</div>}>
              <Outlet />
            </Suspense>
          ),
          children: [
            // Add index route that redirects to first child
            ...(firstChildKey ? [{ index: true, element: <IndexRedirect to={firstChildKey} /> }] : []),
            ...RoutingTransformer(routeItem.routes),
            { path: '*', element: <Navigate to="/404" replace /> }, // Fallback route
          ], // Recursively process child routes
        };
      }

      // If it's a direct route, return it
      if (routeItem.route) {
        return {
          path: routeKey,
          element: <routeItem.route />,
        };
      }

      return null; // Skip if neither `routes` nor `route` exist
    })
    .filter(Boolean) as RouteObject[]; // Remove null values
  return transformed;
};

export default RoutingTransformer;
